import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { I18NService } from '@core';
import { DA_SERVICE_TOKEN } from '@delon/auth';
import { ALAIN_I18N_TOKEN, I18nPipe, SettingsService, User } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalService } from 'ng-zorro-antd/modal';
import { finalize } from 'rxjs';
import { AvatarFactory } from 'src/app/factory/avatar-factory';
import { HttpService } from 'src/app/http/http.service';
import { GlobalGuardService } from 'src/app/providers/global-guard.service';
import { CommonService } from 'src/app/services/common.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'header-user',
  template: `
    <div
      class="alain-default__nav-item d-flex align-items-center px-sm"
      nz-dropdown
      nzPlacement="bottomRight"
      [nzDropdownMenu]="userMenu"
      nzTrigger="click"
      [testId]="'user-dropdown-trigger'"
    >
      <nz-avatar [nzSrc]="getAvatarUrl()" nzSize="small" nzShape="circle" class="mr-sm" [testId]="'user-avatar'" />
      <span class="user-name-text" [title]="user.name" [testId]="'user-name'">{{ user.name }}</span>
    </div>
    <nz-dropdown-menu #userMenu="nzDropdownMenu">
      <div nz-menu class="width-sm" [testId]="'user-dropdown-menu'">
        <!-- <div nz-menu-item routerLink="/pro/account/center">
          <i nz-icon nzType="user" class="mr-sm"></i>
          {{ 'menu.account.center' | i18n }}
        </div>
        <div nz-menu-item routerLink="/pro/account/settings">
          <i nz-icon nzType="setting" class="mr-sm"></i>
          {{ 'menu.account.settings' | i18n }}
        </div>
        <div nz-menu-item routerLink="/exception/trigger">
          <i nz-icon nzType="close-circle" class="mr-sm"></i>
          {{ 'menu.account.trigger' | i18n }}
        </div> -->
        <li nz-menu-divider></li>
        <div nz-menu-item (click)="logout()" [testId]="'logout-menu-item'">
          <i nz-icon nzType="logout" class="mr-sm" [testId]="'logout-icon'"></i>
          {{ 'menu.account.logout' | i18n }}
        </div>
      </div>
    </nz-dropdown-menu>
  `,
  styles: [
    `
      .user-name-text {
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzDropDownModule, NzMenuModule, NzIconModule, I18nPipe, NzAvatarModule, TestIdDirective]
})
export class HeaderUserComponent {
  private readonly http = inject(HttpService);
  private readonly settings = inject(SettingsService);
  private readonly router = inject(Router);
  private readonly tokenService = inject(DA_SERVICE_TOKEN);
  private readonly modalService = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly globalGuardService = inject(GlobalGuardService);
  private readonly commonService = inject(CommonService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  isLoggingOut = false;

  get user(): User {
    const loginInfo = localStorage.getItem('loginInfo');
    if (loginInfo) {
      const loginInfoObj = JSON.parse(loginInfo);
      return { name: loginInfoObj.name, avatar: '' };
    }
    return this.settings.user;
  }

  /**
   * 获取头像URL，如果没有头像则生成文字头像
   */
  getAvatarUrl(): string {
    const currentUser = this.user;
    if (currentUser.avatar && currentUser.avatar.trim() !== '') {
      return currentUser.avatar;
    }
    return this.getDefaultAvatar();
  }

  /**
   * 生成默认文字头像
   */
  getDefaultAvatar(): string {
    const currentUser = this.user;
    const userName = currentUser.name || this.i18n.fanyi('user.default-name');
    return AvatarFactory.defaultAvatarWithName(userName);
  }

  async logout(): Promise<void> {
    // 防止多次同時登出
    if (this.isLoggingOut) {
      return;
    }

    // 检查是否可以离开当前页面
    const canLeave = await this.globalGuardService.checkCanDeactivate();

    if (!canLeave) {
      return; // 如果不能离开，取消登出操作
    }

    // 清理当前页面的未保存状态
    const currentComponent = this.globalGuardService.getCurrentComponent();
    if (currentComponent && 'hasUnsavedChanges' in currentComponent) {
      (currentComponent as any).hasUnsavedChanges = false;

      if (typeof (currentComponent as any).resetChangeDetection === 'function') {
        (currentComponent as any).resetChangeDetection();
      }
    }

    // 显示全屏加載遮罩
    this.isLoggingOut = true;
    const modal = this.modalService.create({
      nzContent: `
        <div style="text-align: center; padding: 20px;">
          <i nz-icon nzType="loading" style="font-size: 24px; color: #1890ff;"></i>
          <p style="margin-top: 12px; font-size: 14px; color: rgba(0, 0, 0, 0.65);">${this.i18n.fanyi('user.logout-loading')}</p>
        </div>
      `,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzCentered: true,
      nzClassName: 'logout-modal',
      nzWidth: 320,
      nzBodyStyle: { padding: '24px 24px 32px' }
    });

    this.http
      .post(ConstantUtil.ACCOUNT_LOGOUT, {})
      .pipe(
        finalize(() => {
          this.isLoggingOut = false;
          // 关闭加载遮罩
          modal.close();
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.tokenService.clear();
            localStorage.removeItem('loginInfo');
            this.commonService.clearTenantInfo();
            localStorage.setItem('isLoggedIn', 'false');
            localStorage.removeItem('menus');
            this.router.navigateByUrl(this.tokenService.login_url!);
          }
        },
        error: error => {
          // 網路錯誤處理 - 不執行登出清理操作
          console.error('Logout failed:', error);
          // finalize 會自動執行，關閉模態框和重置狀態
        }
      });
  }
}
